/**
 * What a tap or click snaps to on the measured model: a corner or the middle of an edge first, then
 * an edge, then the surface under the pointer. All in the hall frame; the caller supplies how a hall
 * point lands on the screen, so this stays plain maths.
 */

import { catalogueItem } from '@mawkeb/shared/catalogue';
import { footprint, placedSize, type Layout } from '@mawkeb/shared/layout';
import type { MeasuredModel, Vec3 } from '@mawkeb/shared/model';

export interface SnapPoint {
  p: Vec3;
  label: string;
}

export interface SnapEdge {
  a: Vec3;
  b: Vec3;
  label: string;
}

export interface SnapFeatures {
  points: SnapPoint[];
  edges: SnapEdge[];
}

export interface SurfaceHit {
  point: Vec3;
  label: string;
}

export interface Snap {
  point: Vec3;
  label: string;
  kind: 'corner' | 'edge' | 'surface';
}

export type Project = (p: Vec3) => [number, number] | null;

const mid = (a: Vec3, b: Vec3): Vec3 => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2];
const lerp = (a: Vec3, b: Vec3, t: number): Vec3 => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

export function buildSnapFeatures(model: MeasuredModel): SnapFeatures {
  const points: SnapPoint[] = [];
  const edges: SnapEdge[] = [];
  const walls = model.walls;
  const short = (name: string) => lower(name).replace(/ wall$/, '');
  walls.forEach((w, i) => {
    const prev = walls[(i - 1 + walls.length) % walls.length]!;
    const a0: Vec3 = [w.a[0], w.a[1], 0];
    const b0: Vec3 = [w.b[0], w.b[1], 0];
    const corner = `Corner of the ${short(prev.name)} and ${short(w.name)} walls`;
    edges.push({ a: a0, b: b0, label: w.name });
    points.push({ p: a0, label: corner });
    points.push({ p: mid(a0, b0), label: `Middle of the ${lower(w.name)}` });
    edges.push({ a: a0, b: [a0[0], a0[1], Math.min(w.height, prev.height)], label: corner });
  });
  for (const s of model.solids) {
    const n = s.outline.length;
    const curved = n > 8;
    for (let i = 0; i < n; i++) {
      const p = s.outline[i]!;
      const q = s.outline[(i + 1) % n]!;
      const edge = `${s.name}${sideOf(s.kind, p, q)}`;
      const top0: Vec3 = [p[0], p[1], s.z1];
      const top1: Vec3 = [q[0], q[1], s.z1];
      const bot0: Vec3 = [p[0], p[1], s.z0];
      const bot1: Vec3 = [q[0], q[1], s.z0];
      edges.push({ a: top0, b: top1, label: edge });
      edges.push({ a: bot0, b: bot1, label: `Foot of the ${lower(edge)}` });
      if (!curved || i % 6 === 0) {
        points.push({ p: top0, label: `${s.name} corner` });
        points.push({ p: bot0, label: `Foot of the ${lower(s.name)} corner` });
        edges.push({ a: bot0, b: top0, label: `${s.name} corner` });
      }
      if (!curved) {
        points.push({ p: mid(top0, top1), label: `Middle of the ${lower(edge)}` });
        points.push({ p: mid(bot0, bot1), label: `Middle of the foot of the ${lower(edge)}` });
      }
    }
    if (curved && s.kind === 'stage') {
      const frontMid = s.outline[Math.floor(n / 2)] ?? s.outline[0]!;
      const front = model.references.find((r) => r.id === 'back_to_stage_front_centre');
      const centre = front ? ([front.b[0], front.b[1]] as const) : frontMid;
      points.push({ p: [centre[0], centre[1], s.z0], label: 'Middle of the foot of the stage front' });
      points.push({ p: [centre[0], centre[1], s.z1], label: 'Middle of the stage front' });
    }
  }
  for (const l of model.lines) {
    const a: Vec3 = [l.a[0], l.a[1], 0];
    const b: Vec3 = [l.b[0], l.b[1], 0];
    edges.push({ a, b, label: l.name });
    points.push({ p: mid(a, b), label: `Middle of the ${lower(l.name)}` });
  }
  return { points, edges };
}

/** Corners and edges of everything placed, at its base and its top, so the tape measures props too. */
export function propSnapFeatures(layout: Layout): SnapFeatures {
  const points: SnapPoint[] = [];
  const edges: SnapEdge[] = [];
  for (const p of layout.placements) {
    const item = catalogueItem(p.item);
    if (!item) continue;
    const name = p.kind === 'rows' ? 'the rows' : `the ${lower(item.name)}`;
    const h = p.kind === 'rows' ? item.size[2] : placedSize(p, item)[2];
    const corners = footprint(p);
    const base: Vec3[] = corners.map(([x, y]) => [x, y, p.aim.z]);
    const top: Vec3[] = corners.map(([x, y]) => [x, y, p.aim.z + h]);
    for (let i = 0; i < 4; i++) {
      const j = (i + 1) % 4;
      points.push({ p: top[i]!, label: `Top corner of ${name}` }, { p: base[i]!, label: `Foot of ${name}` });
      edges.push({ a: top[i]!, b: top[j]!, label: `Top edge of ${name}` }, { a: base[i]!, b: top[i]!, label: `Corner of ${name}` });
      points.push({ p: mid(top[i]!, top[j]!), label: `Middle of the top edge of ${name}` });
    }
  }
  return { points, edges };
}

/** Which side of the stage an outline edge is, from the way it faces (outlines run anticlockwise). */
function sideOf(kind: string, p: readonly number[], q: readonly number[]): string {
  if (kind !== 'stage') return kind === 'column' ? '' : ' wall';
  const nx = q[1]! - p[1]!;
  const ny = -(q[0]! - p[0]!);
  const len = Math.hypot(nx, ny) || 1;
  if (ny / len < -0.7) return ' front';
  if (ny / len > 0.7) return ' back';
  return nx < 0 ? ' left side' : ' right side';
}

/** A snap label as a short name for the list of measurements: "Middle of the back wall" reads "Back wall". */
export function shortLabel(label: string): string {
  const s = label.replace(/^Middle of the /, '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function closestOnScreenSegment(px: number, py: number, a: [number, number], b: [number, number]): { t: number; d: number } {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - a[0]) * dx + (py - a[1]) * dy) / len2)) : 0;
  return { t, d: Math.hypot(px - (a[0] + t * dx), py - (a[1] + t * dy)) };
}

/**
 * The best snap near the pointer: the nearest corner or edge on screen, with corners given a head
 * start so they catch when the pointer is close to them, but never over an edge right under it.
 * `visible` says whether a hall point can be seen from the camera, so snapping never jumps to the
 * far side of a column.
 */
export function snap(
  pointer: [number, number],
  hit: SurfaceHit | null,
  features: SnapFeatures,
  project: Project,
  radiusPx: number,
  visible: (p: Vec3) => boolean = () => true,
): Snap | null {
  // a tap near a corner is also beside the edges that meet there: the corner is what was meant
  const cornerHeadStart = radiusPx * 0.6;
  const candidates: { score: number; snap: () => Snap | null }[] = [];
  for (const sp of features.points) {
    const s = project(sp.p);
    if (!s) continue;
    const d = Math.hypot(s[0] - pointer[0], s[1] - pointer[1]);
    if (d < radiusPx) candidates.push({ score: d - cornerHeadStart, snap: () => (visible(sp.p) ? { point: sp.p, label: sp.label, kind: 'corner' } : null) });
  }
  for (const e of features.edges) {
    const sa = project(e.a);
    const sb = project(e.b);
    if (!sa || !sb) continue;
    const c = closestOnScreenSegment(pointer[0], pointer[1], sa, sb);
    if (c.d < radiusPx * 0.75) {
      candidates.push({
        score: c.d,
        snap: () => {
          // Screen-space t is close to, but not exactly, the 3D t under perspective; refine on the segment.
          const p = refineOnEdge(e, pointer, project, c.t);
          return visible(p) ? { point: p, label: e.label, kind: 'edge' } : null;
        },
      });
    }
  }
  candidates.sort((a, b) => a.score - b.score);
  for (const c of candidates) {
    const s = c.snap();
    if (s) return s;
  }
  return hit ? { point: hit.point, label: hit.label, kind: 'surface' } : null;
}

function refineOnEdge(e: SnapEdge, pointer: [number, number], project: Project, t0: number): Vec3 {
  let lo = Math.max(0, t0 - 0.25);
  let hi = Math.min(1, t0 + 0.25);
  const dist = (t: number) => {
    const s = project(lerp(e.a, e.b, t));
    return s ? Math.hypot(s[0] - pointer[0], s[1] - pointer[1]) : Infinity;
  };
  for (let i = 0; i < 40; i++) {
    const m1 = lo + (hi - lo) / 3;
    const m2 = hi - (hi - lo) / 3;
    if (dist(m1) < dist(m2)) hi = m2;
    else lo = m1;
  }
  return lerp(e.a, e.b, (lo + hi) / 2);
}

/** Holds the second end square to the hall: along whichever of x, y or z the line mostly runs. */
export function straighten(a: Vec3, b: Vec3): Vec3 {
  const d = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const axis = d.map(Math.abs).indexOf(Math.max(...d.map(Math.abs)));
  const out: Vec3 = [a[0], a[1], a[2]];
  out[axis] = b[axis]!;
  return out;
}

export function distance3(a: Vec3, b: Vec3): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
}
